import { useCallback, useEffect, useState } from 'react';
import { Clock, LockKey, PencilSimple, User } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import ChangePasswordModal from '../components/modals/ChangePasswordModal';
import EditProfileModal from '../components/modals/EditProfileModal';
import ThemeSettings from '../components/settings/ThemeSettings';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Pagination from '../components/ui/Pagination';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { FormatDateTimeBr, FormatIsoDate } from '../utils/date';

const LOGIN_HISTORY_DEFAULT_LIMIT = 5;

const Profile = () => {
    const { user, refreshUser } = useAuth();
    const [loginHistory, setLoginHistory] = useState([]);
    const [loginHistoryPage, setLoginHistoryPage] = useState(1);
    const [loginHistoryPaging, setLoginHistoryPaging] = useState({
        total: 0,
        limit: LOGIN_HISTORY_DEFAULT_LIMIT,
        offset: 0,
    });
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const toast = useToast();

    const fetchLoginHistory = useCallback(
        async (pageToFetch = 1) => {
            const safePage = Math.max(1, Number(pageToFetch) || 1);
            const limit = Math.max(1, Number(loginHistoryPaging.limit || LOGIN_HISTORY_DEFAULT_LIMIT));
            const offset = (safePage - 1) * limit;

            try {
                setLoading(true);
                const data = await api.getProfileLoginHistory({ limit, offset });
                setLoginHistory(Array.isArray(data?.results) ? data.results : []);
                setLoginHistoryPaging({
                    total: Number(data?.paging?.total || 0),
                    limit: Number(data?.paging?.limit || limit),
                    offset: Number(data?.paging?.offset || offset),
                });
            } catch (_error) {
                // Silently handle error
                setLoginHistory([]);
                setLoginHistoryPaging({
                    total: 0,
                    limit,
                    offset,
                });
            } finally {
                setLoading(false);
            }
        },
        [loginHistoryPaging.limit],
    );

    useEffect(() => {
        fetchLoginHistory(loginHistoryPage);
    }, [fetchLoginHistory, loginHistoryPage]);

    const formatDate = (dateString) => FormatIsoDate(dateString, { blank: '-' });
    const formatDateTime = (dateString) => FormatDateTimeBr(dateString, { blank: '-' });

    const parseUserAgent = (ua) => {
        if (!ua) {
            return 'Desconhecido';
        }
        if (ua.includes('Chrome')) {
            return 'Chrome';
        }
        if (ua.includes('Firefox')) {
            return 'Firefox';
        }
        if (ua.includes('Safari')) {
            return 'Safari';
        }
        if (ua.includes('Edge')) {
            return 'Edge';
        }
        return 'Outro';
    };

    const handleProfileUpdate = (message) => {
        toast.success(message);
        refreshUser?.();
    };

    const handlePasswordChange = (message) => {
        toast.success(message);
    };

    const totalHistoryPages = Math.max(
        1,
        Math.ceil(
            Number(loginHistoryPaging.total || 0) /
                Math.max(1, Number(loginHistoryPaging.limit || LOGIN_HISTORY_DEFAULT_LIMIT)),
        ),
    );
    const currentHistoryPage =
        Math.floor(
            Number(loginHistoryPaging.offset || 0) /
                Math.max(1, Number(loginHistoryPaging.limit || LOGIN_HISTORY_DEFAULT_LIMIT)),
        ) + 1;

    return (
        <div className="space-y-6 pb-20 lg:pb-6">
            <PageHeader title="Meu perfil" subtitle="Dados da conta, segurança e histórico de acessos" />

            {/* User Info Card */}
            <Card>
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                    <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                        <div className="w-16 h-16 lg:w-20 lg:h-20 bg-mono-200 dark:bg-mono-800 rounded-full flex items-center justify-center flex-shrink-0">
                            <User size={32} className="lg:w-10 lg:h-10 text-mono-600 dark:text-mono-400" />
                        </div>
                        <div className="text-center sm:text-left">
                            <h2 className="text-xl lg:text-2xl font-bold text-black dark:text-white">
                                {user?.full_name}
                            </h2>
                            <p className="text-sm lg:text-base text-mono-600 dark:text-mono-400">{user?.email}</p>
                            <div className="mt-2 text-xs text-mono-600 dark:text-mono-400">
                                Permissoes ativas: {user?.permissions?.length || 0}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowEditModal(true)}
                            className="flex-1 sm:flex-none"
                        >
                            <PencilSimple size={16} className="sm:mr-2" />
                            <span className="hidden sm:inline">Editar</span>
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setShowPasswordModal(true)}
                            className="flex-1 sm:flex-none"
                        >
                            <LockKey size={16} className="sm:mr-2" />
                            <span className="hidden sm:inline">Senha</span>
                        </Button>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                    <div>
                        <p className="text-xs lg:text-sm text-mono-600 dark:text-mono-400">Membro desde</p>
                        <p className="text-sm lg:text-base font-medium text-black dark:text-white">
                            {formatDate(user?.created_at)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs lg:text-sm text-mono-600 dark:text-mono-400">Ultimo acesso</p>
                        <p className="text-sm lg:text-base font-medium text-black dark:text-white">
                            {formatDateTime(user?.last_login)}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs lg:text-sm text-mono-600 dark:text-mono-400">Ultimo IP</p>
                        <p className="text-sm lg:text-base font-medium font-mono text-black dark:text-white">
                            {user?.last_ip || '-'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs lg:text-sm text-mono-600 dark:text-mono-400">Status</p>
                        <p className="text-sm lg:text-base font-medium text-black dark:text-white">
                            {user?.is_active ? 'Ativo' : 'Inativo'}
                        </p>
                    </div>
                </div>
            </Card>

            {/* Theme Settings */}
            <ThemeSettings />

            {/* Login History */}
            <Card>
                <h3 className="text-lg font-bold text-black dark:text-white mb-4">Historico de Acessos</h3>
                {loading ? (
                    <p className="text-sm text-mono-600 dark:text-mono-400">Carregando...</p>
                ) : loginHistory.length === 0 ? (
                    <p className="text-sm text-mono-600 dark:text-mono-400">Nenhum historico disponivel</p>
                ) : (
                    <div className="space-y-3">
                        {loginHistory.map((login, index) => (
                            <div
                                key={login.id || index}
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-3 border-b border-mono-200 dark:border-mono-800 last:border-0"
                            >
                                <div className="flex items-center gap-3">
                                    <Clock size={18} className="text-mono-500 dark:text-mono-500 flex-shrink-0" />
                                    <div>
                                        <p className="text-sm font-medium text-black dark:text-white">
                                            {formatDateTime(login.login_at)}
                                        </p>
                                        <p className="text-xs text-mono-600 dark:text-mono-400 font-mono">
                                            {login.ip_address || '-'}
                                        </p>
                                    </div>
                                </div>
                                <div className="text-xs sm:text-sm text-mono-600 dark:text-mono-400 pl-9 sm:pl-0">
                                    {parseUserAgent(login.user_agent)}
                                </div>
                            </div>
                        ))}

                        {Number(loginHistoryPaging.total || 0) > 0 ? (
                            <Pagination
                                currentPage={currentHistoryPage}
                                totalPages={totalHistoryPages}
                                onPageChange={setLoginHistoryPage}
                                itemsPerPage={Number(loginHistoryPaging.limit || LOGIN_HISTORY_DEFAULT_LIMIT)}
                                totalItems={Number(loginHistoryPaging.total || 0)}
                            />
                        ) : null}
                    </div>
                )}
            </Card>

            {/* Modals */}
            <EditProfileModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                currentUser={user}
                onSuccess={handleProfileUpdate}
            />

            <ChangePasswordModal
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordChange}
            />
        </div>
    );
};

export default Profile;
